/**
 * Enumé - Main Application
 *
 * A student collaboration platform for discovering unexpected creative connections.
 * Real-time enabled with SSE for messaging, notifications, and presence.
 *
 * v9 Changes (vs v8):
 * - Surprise Me is now the mobile home — first tab in mobile nav
 * - Mobile users land on /surprise by default, desktop on /dashboard
 * - Map moves to second position in mobile nav (still first on desktop)
 * - Challenges stays in mobile nav
 * - Desktop nav unchanged
 * - Removed JourneyPage (lineage + versions now live in ProjectDetailOverlay)
 * - /journey route removed, import removed, desktop menu item removed
 *
 * v10 Changes (vs v9):
 * - Added /terms, /privacy, /dmca public routes (legal pages)
 * - Added TermsPage, PrivacyPage, DMCAPage imports
 *
 * v11 Changes (vs v10):
 * - Added TosGate import
 * - ProtectedRoute now checks user.tosAccepted — existing users without TOS
 *   acceptance see a legal interstitial before accessing any protected route
 *
 * v12 Changes (vs v11):
 * - Added /stats public route — PlatformStatsPage (no auth, no AppLayout)
 * - Public-facing statistics dashboard proving the cross-pollination thesis
 *
 * v13 Changes (vs v12):
 * - Added /admin route — AdminDashboard (admin-gated, AppLayout)
 * - Added AdminDashboard import
 * - Added LayoutDashboard to lucide imports
 * - Added Admin Dashboard link in DesktopNav user menu (only visible to admins)
 *
 * v14 Changes (vs v13):
 * - Added /submit public route — SubmitPage (cohort upload portal, no AppLayout)
 * - Added /cohort/:slug protected route — CohortDashboard (AppLayout)
 * - Added SubmitPage, CohortDashboard imports
 *
 * v15 Changes (vs v14):
 * - Added /acceptable-use public route — AcceptableUsePage
 * - Added AcceptableUsePage import
 * - ProtectedRoute gate also checks user.tosCurrent so existing users
 *   re-accept the Terms whenever the backend bumps CURRENT_TOS_VERSION.
 *   If the backend doesn't send tosCurrent yet (undefined), the gate falls
 *   back to the old tosAccepted check, so this is safe to deploy first.
 *
 * Place at: src/App.jsx
 */

import React, { useState } from 'react';
import {
  BrowserRouter as Router, Routes, Route,
  Navigate, useNavigate, useLocation
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import toast, { Toaster } from 'react-hot-toast';
import {
  Home, MessageCircle, Target, Plus, User, LogOut,
  Sparkles, Menu, X, Settings, Shield, Wifi, WifiOff, Search, Bell,
  Shuffle, LayoutDashboard, GraduationCap
} from 'lucide-react';

import { AuthProvider, useAuth }         from './context/AuthContext';
import { RealtimeProvider, useRealtime } from './context/RealtimeContext';
import { TrayProvider }                  from './context/TrayContext';
import { PlatformProvider }              from './context/PlatformContext';
import { NSFWProvider }                  from './components/nsfw/NSFWFilter';
import { cn }                            from './utils/cn';
import { Button, Avatar }                from './components/ui';

// Pages
import Dashboard          from './pages/Dashboard';
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import SettingsPage       from './pages/SettingsPage';
import ProfilePage        from './pages/ProfilePage';
import MessagesPage       from './pages/MessagesPage';
import ExplorePage        from './pages/ExplorePage';
import ChallengesPage     from './pages/ChallengesPage';
import SurpriseMePage     from './pages/SurpriseMePage';
import NotificationsPage  from './pages/NotificationsPage';
import NotificationBell   from './components/NotificationBell';
import LandingPage        from './pages/LandingPage';
import TermsPage          from './pages/TermsPage';
import PrivacyPage        from './pages/PrivacyPage';
import DMCAPage           from './pages/DMCAPage';
import AcceptableUsePage  from './pages/AcceptableUsePage';
import TosGate            from './components/TosGate';
import ForgotPasswordPage   from './pages/ForgotPasswordPage';
import ResetPasswordPage    from './pages/ResetPasswordPage';
import PlatformStatsPage    from './pages/PlatformStatsPage';
import AdminDashboard       from './pages/AdminDashboard';
import APIPage from './pages/APIPage';
import ZonePage from './pages/ZonePage';
import SubmitPage from './pages/SubmitPage';
import CohortDashboard from './pages/CohortDashboard';
import MyCohortsPage from './pages/MyCohortsPage';
import PricingPage from './pages/PricingPage';

// Shareable story — self-contained, public, no props needed
import { LineageStoryPage } from './components/journey/ShareableLineageStory';

// ============================================================================
// HELPERS
// ============================================================================

/** True when the viewport is narrower than Tailwind's md breakpoint (768px). */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
};

// ============================================================================
// CONNECTION STATUS INDICATOR
// ============================================================================

const ConnectionIndicator = () => {
  let isConnected      = false;
  let connectionStatus = 'disconnected';

  try {
    const rt       = useRealtime();
    isConnected      = rt.isConnected;
    connectionStatus = rt.connectionStatus;
  } catch { /* Not in provider */ }

  if (isConnected) return null;

  return (
    <div className={cn(
      'fixed bottom-20 md:bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-500',
      connectionStatus === 'connecting'
        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
        : 'bg-red-500/10 text-red-400 border border-red-500/20',
    )}>
      {connectionStatus === 'connecting' ? (
        <>
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          Reconnecting...
        </>
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          Offline &mdash; updates may be delayed
        </>
      )}
    </div>
  );
};

// ============================================================================
// MOBILE NAV — Surprise Me is the first tab (mobile home)
// ============================================================================

const MobileNav = () => {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();

  if (!user) return null;

  const navItems = [
    { path: '/surprise',      label: 'Discover',   icon: Sparkles      },
    { path: '/dashboard',     label: 'Map',         icon: Home          },
    { path: '/challenges',    label: 'Challenges',  icon: Target        },
    { path: '/explore',       label: 'Explore',     icon: Search        },
    { path: '/messages',      label: 'Chat',        icon: MessageCircle },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800/50 safe-area-bottom">
      <div className="flex justify-around items-center h-16 px-2">
        {navItems.map((item) => {
          const Icon   = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative flex flex-col items-center justify-center w-16 h-14 rounded-2xl transition-all"
            >
              {active && (
                <div className="absolute -top-0.5 w-6 h-1 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" />
              )}
              <Icon
                className={cn('w-5 h-5 transition-colors', active ? 'text-purple-400' : 'text-zinc-500')}
                strokeWidth={active ? 2.5 : 2}
              />
              <span className={cn(
                'text-[10px] mt-1 font-medium transition-colors',
                active ? 'text-purple-300' : 'text-zinc-500',
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// ============================================================================
// FAB
// ============================================================================

const FloatingActionButton = () => {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();

  if (!user) return null;
  if (location.pathname === '/dashboard') return null;

  return (
    <FABInner navigate={navigate} />
  );
};

const FABInner = ({ navigate }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      {expanded && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setExpanded(false)}
        />
      )}

      <div className="md:hidden fixed bottom-20 right-4 z-50 safe-area-bottom">
        <div className={cn(
          'absolute bottom-14 right-0 flex flex-col gap-3 transition-all duration-200',
          expanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none',
        )}>
          <button
            onClick={() => { navigate('/dashboard?create-challenge'); setExpanded(false); }}
            className="flex items-center gap-2 pl-4 pr-5 py-2.5 bg-amber-500/90 backdrop-blur rounded-full text-white text-sm font-medium shadow-lg shadow-amber-500/20"
          >
            <Target className="w-4 h-4" />
            Challenge
          </button>
          <button
            onClick={() => { navigate('/dashboard?create'); setExpanded(false); }}
            className="flex items-center gap-2 pl-4 pr-5 py-2.5 bg-purple-500/90 backdrop-blur rounded-full text-white text-sm font-medium shadow-lg shadow-purple-500/20"
          >
            <Plus className="w-4 h-4" />
            Project
          </button>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center',
            'bg-gradient-to-br from-purple-600 to-indigo-600 shadow-lg shadow-purple-600/30',
            'transition-transform duration-200 active:scale-95',
            expanded && 'rotate-45',
          )}
        >
          <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
        </button>
      </div>
    </>
  );
};

// ============================================================================
// DESKTOP NAV — unchanged, map is still home on desktop
// ============================================================================

const DesktopNav = () => {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const location         = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  if (!user) return null;

  const navItems = [
    { path: '/dashboard',  label: 'Map',        icon: Home          },
    { path: '/explore',    label: 'Explore',    icon: Search        },
    { path: '/challenges', label: 'Challenges', icon: Target        },
    { path: '/surprise',   label: 'Surprise Me', icon: Shuffle      },
    { path: '/messages',   label: 'Messages',   icon: MessageCircle },
    { path: '/cohorts',    label: 'Classes',    icon: GraduationCap },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 h-16 bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800/50">
      <div className="flex items-center justify-between w-full max-w-7xl mx-auto px-6">

        {/* Logo */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 group"
        >
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/10 group-hover:shadow-purple-500/25 transition-shadow">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">Enumé</span>
        </button>

        {/* Nav items */}
        <div className="flex items-center gap-1">
          {navItems.map((item) => {
            const Icon   = item.icon;
            const active = isActive(item.path);

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                  active
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50',
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <NotificationBell />

          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-zinc-800/50 transition-colors"
            >
              <Avatar
                src={user.profile_picture_url || user.profilePictureUrl}
                alt={user.username}
                size="sm"
              />
              <span className="text-sm font-medium text-zinc-300 hidden lg:inline">
                {user.username}
              </span>
            </button>

            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  <button
                    onClick={() => { navigate('/profile');       setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </button>
                  <button
                    onClick={() => { navigate('/notifications'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    <Bell className="w-4 h-4" />
                    Notifications
                  </button>
                  <button
                    onClick={() => { navigate('/settings');      setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={() => { navigate('/settings?tab=content'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    Content Preferences
                  </button>
                  {user?.isAdmin && (
                    <button
                      onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-purple-400 hover:bg-zinc-800 transition-colors"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Admin Dashboard
                    </button>
                  )}
                  <div className="h-px bg-zinc-800" />
                  <button
                    onClick={() => { logout(); navigate('/'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-400 hover:bg-zinc-800 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// ============================================================================
// PROTECTED ROUTE
// ============================================================================

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, refreshUser, updateUser } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center animate-pulse">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Gate: users who haven't accepted the CURRENT Terms version.
  // tosCurrent comes from the backend; undefined (older backend) falls
  // back to the old boolean check so the gate never locks everyone out.
  if (user && (!user.tosAccepted || user.tosCurrent === false)) {
    return (
      <TosGate
        user={user}
        onAccepted={async () => {
          // Optimistically clear the gate, then try a full refresh
          updateUser({ tosAccepted: true, tosCurrent: true });
          try { await refreshUser(); } catch { /* /me may 500 — gate is already cleared */ }
        }}
      />
    );
  }

  return children;
};

// ============================================================================
// RESPONSIVE HOME REDIRECT
// Sends mobile users to /surprise, desktop users to /dashboard
// ============================================================================

const ResponsiveHomeRedirect = () => {
  const isMobile = useIsMobile();
  return <Navigate to={isMobile ? '/surprise' : '/dashboard'} replace />;
};

// ============================================================================
// APP LAYOUT
// ============================================================================

const AppLayout = ({ children }) => (
  <div className="min-h-screen bg-zinc-950">
    <DesktopNav />
    <main className="md:pt-16 pb-20 md:pb-0">
      {children}
    </main>
    <MobileNav />
    <FloatingActionButton />
    <ConnectionIndicator />
  </div>
);

// MapLayout — identical nav to AppLayout, but main fills the remaining
// viewport height with no scroll padding so the cosmic map is full-screen.
const MapLayout = ({ children }) => (
  <div className="h-screen flex flex-col bg-zinc-950 overflow-hidden">
    <DesktopNav />
    <main className="flex-1 md:pt-16 overflow-hidden">
      {children}
    </main>
    <MobileNav />
    <ConnectionIndicator />
  </div>
);

// ============================================================================
// ERROR BOUNDARY
// ============================================================================

const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-600/20 to-indigo-600/20 flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-purple-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
      <p className="text-zinc-400 mb-6 text-sm">
        {error?.message || 'An unexpected error occurred'}
      </p>
      <Button onClick={resetErrorBoundary}>Try Again</Button>
    </div>
  </div>
);

// ============================================================================
// MAIN APP
// ============================================================================

const App = () => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <AuthProvider>
      <NSFWProvider>
        <RealtimeProvider>
          <Router>
            <PlatformProvider>
            <TrayProvider>
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background:   '#18181b',
                    color:        '#fff',
                    border:       '1px solid #27272a',
                    borderRadius: '12px',
                    padding:      '12px 16px',
                  },
                }}
              />

              <Routes>
                {/* ── Public routes ────────────────────────────────────────── */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login"    element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
                <Route path="/reset-password"   element={<ResetPasswordPage />} />
                <Route path="/terms"    element={<TermsPage />} />
                <Route path="/privacy"  element={<PrivacyPage />} />
                <Route path="/dmca"     element={<DMCAPage />} />
                <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                <Route path="/api" element={<APIPage />} />
                <Route path="/pricing" element={<PricingPage />} />

                {/*
                 * /lineage/:rootId — PUBLIC, no auth, no AppLayout chrome.
                 * Works for logged-out visitors who receive a shared link.
                 * LineageStoryPage reads rootId from useParams() and fetches its own data.
                 */}
                <Route path="/lineage/:rootId" element={<LineageStoryPage />} />

                {/*
                 * /stats — PUBLIC, no auth, no AppLayout chrome.
                 * Platform statistics dashboard proving creative cross-pollination.
                 * Designed for pitching to institutions and sharing publicly.
                 */}
                <Route path="/stats" element={<PlatformStatsPage />} />

                {/*
                 * /submit — PUBLIC, no auth required to VIEW.
                 * Cohort upload portal. Handles auth states internally:
                 * shows login/register links if not authenticated,
                 * join button if authenticated but not a member,
                 * upload form if authenticated and a member.
                 * No AppLayout — standalone page like /stats.
                 */}
                <Route path="/submit" element={<SubmitPage />} />

                {/* ── Dashboard — full screen map ───────────────────────────── */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <MapLayout><Dashboard /></MapLayout>
                  </ProtectedRoute>
                } />

                {/* ── Explore ──────────────────────────────────────────────── */}
                <Route path="/explore" element={
                  <ProtectedRoute>
                    <AppLayout><ExplorePage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Challenges — first-class collaboration discovery ────── */}
                <Route path="/challenges" element={
                  <ProtectedRoute>
                    <AppLayout><ChallengesPage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Surprise Me — mobile home ────────────────────────────── */}
                <Route path="/surprise" element={
                  <ProtectedRoute>
                    <AppLayout><SurpriseMePage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Legacy redirects ─────────────────────────────────────── */}
                <Route path="/stumble" element={<Navigate to="/surprise" replace />} />

                {/* ── Messages ─────────────────────────────────────────────── */}
                <Route path="/messages" element={
                  <ProtectedRoute>
                    <AppLayout><MessagesPage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Notifications ────────────────────────────────────────── */}
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <AppLayout><NotificationsPage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Profile ──────────────────────────────────────────────── */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <AppLayout><ProfilePage /></AppLayout>
                  </ProtectedRoute>
                } />
                <Route path="/profile/:userId" element={
                  <ProtectedRoute>
                    <AppLayout><ProfilePage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Settings ─────────────────────────────────────────────── */}
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <AppLayout><SettingsPage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Admin ────────────────────────────────────────────────── */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AppLayout><AdminDashboard /></AppLayout>
                  </ProtectedRoute>
                } />

                <Route path="/cohorts" element={
                  <ProtectedRoute>
                    <AppLayout><MyCohortsPage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Cohort Dashboard — classroom view with taxonomy ─────── */}
                <Route path="/cohort/:slug" element={
                  <ProtectedRoute>
                    <AppLayout><CohortDashboard /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Zone Home — niche community page ────────────────────── */}
                <Route path="/zone/:zoneId" element={
                  <ProtectedRoute>
                    <AppLayout><ZonePage /></AppLayout>
                  </ProtectedRoute>
                } />

                {/* ── Convenience redirects ────────────────────────────────── */}

                {/* ── Catch-all: responsive redirect ──────────────────────── */}
                <Route path="*" element={
                  <ProtectedRoute>
                    <ResponsiveHomeRedirect />
                  </ProtectedRoute>
                } />
              </Routes>
            </TrayProvider>
           </PlatformProvider>
          </Router>
        </RealtimeProvider>
      </NSFWProvider>
    </AuthProvider>
  </ErrorBoundary>
);

export default App;